import React, { useRef, useEffect, useState } from 'react';
import { GameMap } from '../../simulation/types/GameMap';
import { SimNPC } from '../../simulation/types/SimNPC';
import { renderMap, RenderConfig } from '../utils/canvasRenderer';

interface MapCanvasProps {
  map: GameMap;
  npcs: SimNPC[];
  tileSize?: number;
  showGrid?: boolean;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

export const MapCanvas: React.FC<MapCanvasProps> = ({
  map,
  npcs,
  tileSize: propTileSize = 40,
  showGrid = true,
  onNPCClick,
  isMobile = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // コンテナサイズを監視してタイルサイズを計算
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(container);
    // 初期サイズを設定
    setContainerSize({
      width: container.clientWidth,
      height: container.clientHeight,
    });

    return () => resizeObserver.disconnect();
  }, []);

  // モバイルではコンテナに合わせてタイルサイズを調整
  const effectiveTileSize = React.useMemo(() => {
    if (!isMobile || containerSize.width === 0) {
      return propTileSize;
    }
    // コンテナ幅に収まるようにタイルサイズを計算（余白を考慮）
    const maxTileWidth = Math.floor((containerSize.width - 4) / map.width);
    const maxTileHeight = Math.floor((containerSize.height - 4) / map.height);
    const calculatedSize = Math.min(maxTileWidth, maxTileHeight, propTileSize);
    return Math.max(calculatedSize, 20); // 最小20px
  }, [isMobile, containerSize, map.width, map.height, propTileSize]);

  const config: RenderConfig = {
    tileSize: effectiveTileSize,
    npcSize: isMobile ? Math.floor(effectiveTileSize * 0.5) : 20,
    showGrid,
  };

  // 描画処理
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    renderMap(ctx, map, npcs, config);
  }, [map, npcs, effectiveTileSize, showGrid, config.npcSize]);

  // クリックハンドラ
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onNPCClick) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // 表示サイズと実際のキャンバスサイズの比率を考慮
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor(((e.clientX - rect.left) * scaleX) / effectiveTileSize);
    const y = Math.floor(((e.clientY - rect.top) * scaleY) / effectiveTileSize);

    // クリック位置のNPCを探す
    const clickedNPC = npcs.find(
      (npc) => npc.position.x === x && npc.position.y === y && npc.isAlive
    );

    if (clickedNPC) {
      onNPCClick(clickedNPC);
    }
  };

  const canvasWidth = map.width * effectiveTileSize;
  const canvasHeight = map.height * effectiveTileSize;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        onClick={handleClick}
        style={{
          border: '2px solid #333',
          cursor: onNPCClick ? 'pointer' : 'default',
          borderRadius: '4px',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      />
    </div>
  );
};
